import { PlusIcon } from '@heroicons/react/24/outline';
import { Trash04Icon } from '@langchain/untitled-ui-icons';
import { Button, IconButton, Modal, ModalClose, ModalDialog } from '@mui/joy';

import { ReactNode, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { CopyMultiButton } from '@/components/CopyButton/CopyMultiButton';
import { DeleteModal } from '@/components/Delete';
import { DisabledTooltip } from '@/components/DisabledTooltip';
import {
  useCurrentOrganization,
  useOrgWorkspaces,
  useOrganizationId,
  useSelectedWorkspace,
  useUpdateWorkspace,
} from '@/hooks/useSwr';
import SimpleEditIcon from '@/icons/SimpleEditIcon.svg?react';
import {
  apiTenantsPath,
  apiWorkspacesPath,
  appOrganizationPath,
} from '@/utils/constants';

import { TIERED_WORKSPACE_UPSELL } from '../utils/constants';
import { CreateWorkspaceModal } from './CreateTenantModal';
import { EditNameForm } from './EditNameForm';

export function EditNameModal(props: {
  className?: string;
  children: (props: { onClick: () => void }) => ReactNode;
  disabled?: boolean;
  defaultName?: string;
}) {
  const [open, setOpen] = useState(false);
  const workspacesQuery = useOrgWorkspaces();
  const names =
    workspacesQuery.data?.map((tenant) => tenant.display_name) ?? [];

  const updateWorkspace = useUpdateWorkspace();

  return (
    <>
      <Modal open={open} onClose={() => setOpen(false)}>
        <ModalDialog
          variant="outlined"
          sx={{ minWidth: 550, paddingX: 5, paddingY: 4 }}
        >
          <ModalClose size="lg" />
          <div className="flex flex-col gap-5">
            <div className=" w-full">
              <EditNameForm
                defaultName={props.defaultName}
                existingNames={names}
                nameTitle="Workspace"
                onSubmit={async (name) => {
                  try {
                    await updateWorkspace.trigger({
                      json: {
                        display_name: name,
                      },
                    });
                    setOpen(false);
                  } catch (e) {
                    console.error('Error updating workspace name', e);
                  }
                }}
                isSubmitting={updateWorkspace.isMutating}
                submitError={updateWorkspace.error}
                buttonLabel="Update"
              />
            </div>
          </div>
        </ModalDialog>
      </Modal>

      {props.children({ onClick: () => setOpen(true) })}
    </>
  );
}

export const WorkspaceSettingsHeader = () => {
  const { data: selectedWorkspace } = useSelectedWorkspace();
  const location = useLocation();
  const navigate = useNavigate();
  const org = useCurrentOrganization();
  const { data: workspaces } = useOrgWorkspaces();

  // find another workspace to navigate to if this workspace gets deleted
  const workspaceToNavigateTo = workspaces.find(
    (workspace) => workspace.id !== selectedWorkspace?.id
  );

  const tier = org.data?.tier ?? 'default';

  const currentURL = location.pathname;
  const upsell =
    TIERED_WORKSPACE_UPSELL[tier] ?? TIERED_WORKSPACE_UPSELL['default'];
  const workspaceId = useOrganizationId();

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  return (
    <div className="flex flex-1 items-center justify-between py-4">
      <div className="flex items-center gap-2">
        <h1 className="text-3xl">{selectedWorkspace?.display_name}</h1>
        <EditNameModal defaultName={selectedWorkspace?.display_name}>
          {({ onClick }) => (
            <SimpleEditIcon
              onClick={onClick}
              className="h-5 w-5 cursor-pointer stroke-ls-black"
            />
          )}
        </EditNameModal>
        <CopyMultiButton
          copyItems={[
            { prefix: 'Workspace ID', item: selectedWorkspace?.id ?? '' },
          ]}
        />
      </div>
      <div className="flex items-center gap-2">
        {currentURL.includes('/workspaces') && (
          <div className="flex gap-2">
            <DeleteModal
              name={selectedWorkspace?.display_name}
              endpoint={`${apiWorkspacesPath}/${workspaceId}`}
              isOpen={isDeleteModalOpen}
              doClose={() => setIsDeleteModalOpen(false)}
              modalTitle="Delete workspace"
              modalText={`This action cannot be undone. This will permanently delete the workspace ${selectedWorkspace?.display_name} and all associated data.`}
              invalidationPrefixes={[
                `${apiWorkspacesPath}`,
                `${apiTenantsPath}`,
              ]}
              onSuccess={() => {
                if (workspaceToNavigateTo) {
                  navigate(
                    `/${appOrganizationPath}/${workspaceToNavigateTo.id}/settings/workspaces/members`
                  );
                }
              }}
              enhancedConfirmation
            />
            <DisabledTooltip
              disabled={workspaces.length === 1}
              disabledMsg={
                'You must have at least 1 workspace in your organization'
              }
            >
              <IconButton
                onClick={() => setIsDeleteModalOpen(true)}
                color="neutral"
                variant="outlined"
                size="sm"
                disabled={workspaces.length === 1}
              >
                <Trash04Icon className="h-4 w-4" />
              </IconButton>
            </DisabledTooltip>
            <CreateWorkspaceModal
              onSuccess={(workspace) => {
                navigate(
                  `/${appOrganizationPath}/${workspace.id}/settings/workspaces/members`
                );
              }}
            >
              {({ onClick }) => (
                <DisabledTooltip
                  disabled={org.data?.reached_max_workspaces ?? false}
                  disabledMsg={upsell.message}
                >
                  <Button
                    disabled={org.data?.reached_max_workspaces ?? false}
                    size="sm"
                    onClick={onClick}
                    startDecorator={<PlusIcon className="h-4 w-4" />}
                  >
                    Workspace
                  </Button>
                </DisabledTooltip>
              )}
            </CreateWorkspaceModal>
          </div>
        )}
      </div>
    </div>
  );
};
